import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import api from "../../../axios/axiosInstance";
import { showToast } from "./ToastSlice";

// ── Countries ─────────────────────────────────────────────────────────────────
export const fetchInventoryCountries = createAsyncThunk(
  "numberInventory/fetchCountries",
  async (_, { rejectWithValue }) => {
    try {
      const res = await api.get("/didlogic/inventory/countries");
      return res.data;
    } catch (err) {
      return rejectWithValue(err.response?.data?.message || "Failed to load countries");
    }
  }
);

// ── Regions/States ────────────────────────────────────────────────────────────
export const fetchRegions = createAsyncThunk(
  "numberInventory/fetchRegions",
  async (countryId, { rejectWithValue }) => {
    try {
      const res = await api.get(`/didlogic/inventory/countries/${countryId}/regions`);
      return res.data;
    } catch (err) {
      return rejectWithValue(err.response?.data?.message || "Failed to load regions");
    }
  }
);

// ── Cities ────────────────────────────────────────────────────────────────────
export const fetchCities = createAsyncThunk(
  "numberInventory/fetchCities",
  async ({ countryId, params = {} }, { rejectWithValue }) => {
    try {
      const res = await api.get(`/didlogic/inventory/countries/${countryId}/cities`, { params });
      return res.data;
    } catch (err) {
      return rejectWithValue(err.response?.data?.message || "Failed to load cities");
    }
  }
);

// ── Numbers by city ───────────────────────────────────────────────────────────
export const fetchNumbersByCity = createAsyncThunk(
  "numberInventory/fetchNumbersByCity",
  async ({ countryId, cityId, params = {} }, { rejectWithValue, dispatch }) => {
    try {
      const res = await api.get(
        `/didlogic/inventory/countries/${countryId}/cities/${cityId}/numbers`,
        { params }
      );
      return res.data;
    } catch (err) {
      const msg = err.response?.data?.message || "Failed to load city numbers";
      dispatch(showToast({ message: msg, variant: "danger" }));
      return rejectWithValue(msg);
    }
  }
);

// ── Advanced search ───────────────────────────────────────────────────────────
// Backend proxies to: GET https://app.didlogic.com/api/v2/numbers/search
// Params: country_name_contains, page, per_page, etc.
export const searchInventoryNumbers = createAsyncThunk(
  "numberInventory/search",
  async (params = {}, { rejectWithValue }) => {
    try {
      // Strip blank values so DIDLogic doesn't reject empty string params
      const clean = Object.fromEntries(
        Object.entries(params).filter(([, v]) => v !== "" && v != null)
      );
      const res = await api.get("/didlogic/inventory/search", { params: clean });
      return res.data;
    } catch (err) {
      return rejectWithValue(err.response?.data?.message || "Search failed");
    }
  }
);

// ── Purchase ──────────────────────────────────────────────────────────────────
// Flow: Frontend → Our Backend → DIDProvider API
//
// The frontend sends a JSON body to our backend.
// Our backend converts it to multipart/form-data and calls DIDProvider:
//   POST https://app.didlogic.com/api/v2/numbers/purchase
// Our backend also handles credit deduction and DIDAssignment recording atomically.
// The API token never leaves the server.
export const purchaseInventoryNumber = createAsyncThunk(
  "numberInventory/purchase",
  async (payload, { rejectWithValue, dispatch }) => {
    try {
      const body = {
        id_in:          [payload.did_id],
        did_numbers:    [String(payload.number)],
        identities:     [{ id: payload.identity_id, did_ids: [payload.did_id] }],
        // Internal metadata used by our backend for credit deduction and DIDAssignment
        monthly_fee:    payload.monthly_fee,
        activation_fee: payload.activation_fee || 0,
        channels:       payload.channels       || 1,
        country:        payload.country,
        area:           payload.area,
      };

      const res = await api.post("/didlogic/inventory/purchase", body);
      dispatch(showToast({ message: `Number +${payload.number} purchased successfully!`, variant: "success" }));
      return res.data;
    } catch (err) {
      dispatch(showToast({ message: err.response?.data?.message || "Purchase failed", variant: "danger" }));
      return rejectWithValue(err.response?.data?.message || "Purchase failed");
    }
  }
);

// ── Slice ─────────────────────────────────────────────────────────────────────
const numberInventorySlice = createSlice({
  name: "numberInventory",
  initialState: {
    // Step 1 — countries
    countries:        [],
    countriesLoading: false,

    // Step 2 — regions (US/Canada etc.)
    regions:        [],
    regionsLoading: false,
    selectedRegion: null,

    // Step 3 — cities
    cities:        [],
    citiesLoading: false,

    // Step 4 — numbers
    numbers:        [],
    numbersTotal:   0,
    numbersPagination: null,
    numbersLoading: false,
    currentKYC:     null,

    // Purchase
    purchasing: false,

    // Navigation state
    selectedCountry: null,
    selectedCity:    null,

    error: null,
  },
  reducers: {
    setSelectedCountry(state, action) {
      state.selectedCountry = action.payload;
      state.regions   = [];
      state.cities    = [];
      state.numbers   = [];
      state.selectedRegion = null;
      state.selectedCity   = null;
      state.currentKYC     = null;
    },
    setSelectedRegion(state, action) {
      state.selectedRegion = action.payload;
      state.cities   = [];
      state.numbers  = [];
      state.selectedCity = null;
    },
    setSelectedCity(state, action) {
      state.selectedCity = action.payload;
      state.numbers  = [];
      state.currentKYC = null;
    },
    clearInventory(state) {
      state.selectedCountry = null;
      state.selectedRegion  = null;
      state.selectedCity    = null;
      state.regions  = [];
      state.cities   = [];
      state.numbers  = [];
      state.currentKYC = null;
    },
    // Instantly populate numbers from an in-memory cache (no API call)
    loadFromCache(state, action) {
      state.numbersLoading    = false;
      state.numbers           = action.payload.data       || [];
      state.numbersTotal      = action.payload.total      ?? 0;
      state.numbersPagination = action.payload.pagination || null;
      state.currentKYC        = action.payload.kyc        || null;
    },
  },
  extraReducers: (builder) => {
    builder
      // Countries
      .addCase(fetchInventoryCountries.pending,   (s) => { s.countriesLoading = true; })
      .addCase(fetchInventoryCountries.fulfilled, (s, a) => {
        s.countriesLoading = false;
        s.countries = a.payload?.data || [];
      })
      .addCase(fetchInventoryCountries.rejected,  (s) => { s.countriesLoading = false; })

      // Regions
      .addCase(fetchRegions.pending,   (s) => { s.regionsLoading = true; })
      .addCase(fetchRegions.fulfilled, (s, a) => {
        s.regionsLoading = false;
        const p = a.payload?.data;
        s.regions = Array.isArray(p) ? p : p?.regions || p?.states || p?.provinces || [];
      })
      .addCase(fetchRegions.rejected,  (s) => { s.regionsLoading = false; })

      // Cities
      .addCase(fetchCities.pending,   (s) => { s.citiesLoading = true; })
      .addCase(fetchCities.fulfilled, (s, a) => {
        s.citiesLoading = false;
        const p = a.payload?.data;
        s.cities = Array.isArray(p) ? p : p?.cities || [];
      })
      .addCase(fetchCities.rejected,  (s) => { s.citiesLoading = false; })

      // Numbers by city
      .addCase(fetchNumbersByCity.pending,   (s) => { s.numbersLoading = true; s.numbers = []; s.numbersTotal = 0; })
      .addCase(fetchNumbersByCity.fulfilled, (s, a) => {
        s.numbersLoading    = false;
        s.numbers           = a.payload?.data       || [];
        s.numbersTotal      = a.payload?.total      ?? 0;
        s.numbersPagination = a.payload?.pagination || null;
        s.currentKYC        = a.payload?.kyc        || null;
      })
      .addCase(fetchNumbersByCity.rejected,  (s) => { s.numbersLoading = false; s.numbers = []; s.numbersTotal = 0; })

      // Search (direct DIDLogic response: { dids: [...], total, pagination })
      .addCase(searchInventoryNumbers.pending,   (s) => { s.numbersLoading = true; })
      .addCase(searchInventoryNumbers.fulfilled, (s, a) => {
        const p = a.payload;
        s.numbersLoading    = false;
        // DIDLogic returns { dids: [...] } or { data: [...] }
        s.numbers           = p?.dids  || p?.data  || (Array.isArray(p) ? p : []);
        s.numbersTotal      = p?.total || p?.count || s.numbers.length;
        s.numbersPagination = p?.pagination || null;
      })
      .addCase(searchInventoryNumbers.rejected,  (s) => { s.numbersLoading = false; })

      // Purchase
      .addCase(purchaseInventoryNumber.pending,   (s) => { s.purchasing = true; })
      .addCase(purchaseInventoryNumber.fulfilled, (s) => { s.purchasing = false; })
      .addCase(purchaseInventoryNumber.rejected,  (s) => { s.purchasing = false; });
  },
});

export const { setSelectedCountry, setSelectedRegion, setSelectedCity, clearInventory, loadFromCache } =
  numberInventorySlice.actions;
export default numberInventorySlice.reducer;
